import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Upload, Loader2, X, Star, GripVertical, Trash2, Image as ImageIcon, Video } from 'lucide-react';
import { useAdminAuth } from '../context/AdminAuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Reusable FileUpload component for admin pages
 * 
 * @param {string} category - Upload category: 'products', 'ads', 'blog', 'ecosystem', 'categories'
 * @param {array} files - Current files array [{id, url, filename, is_primary, order, media_type}]
 * @param {function} onChange - Callback when files change
 * @param {boolean} multiple - Allow multiple files (default: true)
 * @param {boolean} allowVideo - Allow video uploads (default: false)
 * @param {boolean} showPrimary - Show primary selection (default: true for multiple)
 * @param {boolean} sortable - Enable drag-and-drop sorting (default: true for multiple)
 * @param {string} label - Custom label text
 */
const FileUpload = ({
  category = 'products',
  files = [],
  onChange,
  multiple = true,
  allowVideo = false,
  showPrimary = true,
  sortable = true,
  label = 'Upload Images'
}) => {
  const [uploading, setUploading] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);
  const fileInputRef = useRef(null);
  const { getAuthHeader } = useAdminAuth();

  // Get full image URL
  const getImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    return `${BACKEND_URL}${url}`;
  };

  // Determine accepted file types
  const getAcceptedTypes = () => {
    const imageTypes = 'image/jpeg,image/png,image/gif,image/webp';
    const videoTypes = 'video/mp4,video/webm';
    return allowVideo ? `${imageTypes},${videoTypes}` : imageTypes;
  };

  // Handle file selection and upload
  const handleFileSelect = async (event) => {
    const selectedFiles = event.target.files;
    if (!selectedFiles || selectedFiles.length === 0) return;

    // If not multiple, only take first file
    const filesToUpload = multiple ? selectedFiles : [selectedFiles[0]];

    setUploading(true);
    const formData = new FormData();
    
    for (let i = 0; i < filesToUpload.length; i++) {
      formData.append('files', filesToUpload[i]);
    }

    try {
      const response = await axios.post(`${API}/upload/${category}`, formData, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        const uploadedFiles = response.data.files || response.data.images || [];
        const newFiles = uploadedFiles.map((file, idx) => ({
          ...file,
          is_primary: !multiple ? true : (files.length === 0 && idx === 0),
          order: files.length + idx
        }));

        if (multiple) {
          onChange([...files, ...newFiles]);
        } else {
          onChange(newFiles);
        }
        
        toast.success(`${filesToUpload.length} file(s) uploaded`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload files');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Remove a file
  const handleRemove = (fileId) => {
    const newFiles = files.filter(f => f.id !== fileId);
    // If removed was primary, set first as primary
    if (newFiles.length > 0 && !newFiles.some(f => f.is_primary)) {
      newFiles[0].is_primary = true;
    }
    onChange(newFiles);
  };

  // Set primary file
  const handleSetPrimary = (fileId) => {
    onChange(files.map(f => ({
      ...f,
      is_primary: f.id === fileId
    })));
  };

  // Drag and drop handlers
  const handleDragStart = (e, fileId) => {
    setDraggedItem(fileId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, fileId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (fileId !== draggedItem) {
      setDragOverItem(fileId);
    }
  };

  const handleDragLeave = () => {
    setDragOverItem(null);
  };

  const handleDrop = (e, targetId) => {
    e.preventDefault();
    if (!draggedItem || draggedItem === targetId) {
      setDraggedItem(null);
      setDragOverItem(null);
      return;
    }

    const newFiles = [...files];
    const draggedIdx = newFiles.findIndex(f => f.id === draggedItem);
    const targetIdx = newFiles.findIndex(f => f.id === targetId);
    
    if (draggedIdx !== -1 && targetIdx !== -1) {
      const [draggedFile] = newFiles.splice(draggedIdx, 1);
      newFiles.splice(targetIdx, 0, draggedFile);
      
      // Update order values
      const reordered = newFiles.map((f, idx) => ({ ...f, order: idx }));
      onChange(reordered);
    }

    setDraggedItem(null);
    setDragOverItem(null);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverItem(null);
  };

  return (
    <div className="space-y-3" data-testid={`file-upload-${category}`}>
      {/* Upload Button */}
      <div>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          accept={getAcceptedTypes()}
          multiple={multiple}
          className="hidden"
          data-testid={`file-input-${category}`}
        />
        <Button
          type="button"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="w-full md:w-auto"
          data-testid={`upload-btn-${category}`}
        >
          {uploading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Uploading...
            </>
          ) : (
            <>
              <Upload className="h-4 w-4 mr-2" />
              {label}
            </>
          )}
        </Button>
        <p className="text-xs text-gray-500 mt-1">
          {allowVideo ? 'Supports: JPG, PNG, GIF, WebP, MP4, WebM' : 'Supports: JPG, PNG, GIF, WebP'} (Max {category === 'ads' || category === 'ecosystem' ? '10' : '5'}MB)
        </p>
      </div>

      {/* Files Preview */}
      {files.length > 0 ? (
        <div>
          {multiple && sortable && files.length > 1 && (
            <p className="text-xs text-gray-500 mb-2">
              <GripVertical className="inline h-3 w-3 mr-1" />
              Drag to reorder
            </p>
          )}
          <div className={`grid gap-3 ${multiple ? 'grid-cols-2 md:grid-cols-4 lg:grid-cols-6' : 'grid-cols-1 max-w-xs'}`}>
            {files.map((file, idx) => (
              <div
                key={file.id}
                draggable={multiple && sortable}
                onDragStart={(e) => handleDragStart(e, file.id)}
                onDragOver={(e) => handleDragOver(e, file.id)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, file.id)}
                onDragEnd={handleDragEnd}
                className={`relative group border-2 rounded-lg overflow-hidden transition-all duration-200 ${
                  file.is_primary && showPrimary ? 'border-green-500 ring-2 ring-green-200' : 'border-gray-200'
                } ${draggedItem === file.id ? 'opacity-50 scale-95' : ''} ${
                  dragOverItem === file.id ? 'border-blue-500 ring-2 ring-blue-200 scale-105' : ''
                } ${multiple && sortable ? 'cursor-move' : ''}`}
                data-testid={`file-item-${idx}`}
              >
                {/* Drag Handle */}
                {multiple && sortable && (
                  <div className="absolute top-1 right-1 z-10 bg-white/80 rounded p-0.5 opacity-50 group-hover:opacity-100">
                    <GripVertical className="h-3 w-3 text-gray-600" />
                  </div>
                )}
                
                {/* Order Number */}
                {multiple && (
                  <div className="absolute bottom-1 right-1 bg-black/60 text-white text-xs px-1.5 py-0.5 rounded z-10">
                    {idx + 1}
                  </div>
                )}

                {/* Media Preview */}
                {file.media_type === 'video' ? (
                  <div className="w-full h-24 bg-gray-900 flex items-center justify-center">
                    <Video className="h-8 w-8 text-white" />
                  </div>
                ) : (
                  <img
                    src={getImageUrl(file.url)}
                    alt={`File ${idx + 1}`}
                    className="w-full h-24 object-cover pointer-events-none"
                    onError={(e) => { e.target.src = 'https://via.placeholder.com/150?text=No+Image'; }}
                  />
                )}

                {/* Hover Actions */}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                  {multiple && showPrimary && (
                    <button
                      type="button"
                      onClick={() => handleSetPrimary(file.id)}
                      className={`p-1.5 rounded ${file.is_primary ? 'bg-green-500' : 'bg-white/80 hover:bg-white'}`}
                      title={file.is_primary ? 'Primary' : 'Set as primary'}
                    >
                      <Star className={`h-4 w-4 ${file.is_primary ? 'text-white fill-white' : 'text-gray-700'}`} />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => handleRemove(file.id)}
                    className="p-1.5 rounded bg-red-500 hover:bg-red-600"
                    title="Remove"
                  >
                    <Trash2 className="h-4 w-4 text-white" />
                  </button>
                </div>

                {/* Primary Badge */}
                {file.is_primary && showPrimary && multiple && (
                  <div className="absolute top-1 left-1 bg-green-500 text-white text-xs px-1.5 py-0.5 rounded">
                    Primary
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="text-center py-6 bg-gray-50 rounded-lg border-2 border-dashed">
          <ImageIcon className="h-8 w-8 mx-auto text-gray-400 mb-2" />
          <p className="text-sm text-gray-500">No files uploaded yet</p>
          <p className="text-xs text-gray-400 mt-1">Click the button above to upload</p>
        </div>
      )}
    </div>
  );
};

export default FileUpload;
